import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApplicationHostTypeService, CommonControlsModule, RomeKendoModule, RomeLibraryModule } from 'rome-library';
import { GdprDialogModule } from '../interfaceTypes/sharedFeatures/gdpr-dialog/gdpr-dialog.module';
import { BrokerTaggingService, DirectTaggingService, TaggingService } from '../tag-manager';
import { ApplicationFooterComponent } from './components/application-footer/application-footer.component';
import { ApplicationHeaderComponent } from './components/application-header/application-header.component';
import { CookieWarningBannerComponent } from './components/cookie-warning-banner/cookie-warning-banner.component';
import { CoreDialogsComponent } from './components/core-dialogs/core-dialogs.component';
import { GdprPrivacyNoticeDialogComponent } from './components/gdpr-privacy-notice-dialog/gdpr-privacy-notice-dialog.component';
import { MessagesNotificationsComponent } from './components/messages-notifications/messages-notifications.component';
import { SessionEndComponent } from './components/session-end/session-end.component';
import { ApplyCssOnScrollDirective } from './directives/apply-css-on-scroll.directive';
import { DisableBodyScrollDirective } from './directives/disable-body-scroll.directive';
import { CookiesPolicyComponent } from './pages/cookies-policy/cookies-policy.component';
import { DeviceInfoPage } from './pages/device-info/device-info.page';
import { ExpiredPasswordPage } from './pages/expired-password/expired-password.page';
import { PasswordChangeRequiredPage } from './pages/password-change-required/password-change-required.page';
import { ForgottenPasswordPage } from './pages/forgotten-password/forgotten-password.page';
import { JunkEmailsComponent } from './pages/junk-emails/junk-emails.component';
import { LoginPage } from './pages/login/login.page';
import { LogoutPage } from './pages/logout/logout.page';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { RedirectFromOldAppPage } from './pages/redirect-from-old-app/redirect-from-old-app.page';
import { ResetForgottenPasswordPage } from './pages/reset-forgotten-password/reset-forgotten-password.page';
import { SessionClosedPage } from './pages/session-closed/session-closed.page';
import { SystemUnavailablePage } from './pages/system-unavailable/system-unavailable.page';
import { UnauthorizedPage } from './pages/unauthorized/unauthorized.page';
import { UnsupportedDevicePage } from './pages/unsupported-device/unsupported-device.page';
import { WritingBusinessPage } from './pages/writing-business/writing-business.page';
import { BrokerStateService } from './services/broker-state.service';
import { BrokerUserService } from './services/broker-user.service';
import { DirectStateService } from './services/direct-state.service';
import { DirectUserService } from './services/direct-user.service';
import { RedirectFromOldAppService } from './services/redirect-from-old-app.service';
import { StateService } from './services/state.service';
import { UserService } from './services/user.service';

const COMPONENTS = [
  ApplicationHeaderComponent,
  ApplicationFooterComponent,
  CookieWarningBannerComponent,
  CoreDialogsComponent,
  SessionClosedPage,
  SessionEndComponent,
  GdprPrivacyNoticeDialogComponent,
  RedirectFromOldAppPage
];

export function serviceFactory(
  applicationHostTypeService: ApplicationHostTypeService,
  brokerUserService: BrokerUserService,
  directUserService: DirectUserService
): UserService {
  if (applicationHostTypeService.isBrokerInterfaceType()) {
    return brokerUserService;
  }

  if (applicationHostTypeService.isDirectInterfaceType()) {
    return directUserService;
  }
}

export function stateServiceFactory(
  applicationHostTypeService: ApplicationHostTypeService,
  brokerStateService: BrokerStateService,
  directStateService: DirectStateService
): StateService {
  if (applicationHostTypeService.isBrokerInterfaceType()) {
    return brokerStateService;
  }

  if (applicationHostTypeService.isDirectInterfaceType()) {
    return directStateService;
  }
}

export function taggingServiceFactory(
  applicationHostTypeService: ApplicationHostTypeService,
  brokerTaggingService: BrokerTaggingService,
  directTaggingService: DirectTaggingService
): TaggingService {
  if (applicationHostTypeService.isBrokerInterfaceType()) {
    return brokerTaggingService;
  }

  if (applicationHostTypeService.isDirectInterfaceType()) {
    return directTaggingService;
  }
}

@NgModule({
  imports: [FormsModule, RomeLibraryModule, RomeKendoModule, CommonModule, CommonControlsModule, GdprDialogModule],
  declarations: [
    COMPONENTS,
    LoginPage,
    LogoutPage,
    DisableBodyScrollDirective,
    ExpiredPasswordPage,
    PasswordChangeRequiredPage,
    ForgottenPasswordPage,
    ResetForgottenPasswordPage,
    UnauthorizedPage,
    MessagesNotificationsComponent,
    JunkEmailsComponent,
    PrivacyPolicyComponent,
    CookiesPolicyComponent,
    UnsupportedDevicePage,
    SystemUnavailablePage,
    ApplyCssOnScrollDirective,
    WritingBusinessPage,
    DeviceInfoPage
  ],
  exports: [COMPONENTS, DisableBodyScrollDirective, RomeLibraryModule, RomeKendoModule, ApplyCssOnScrollDirective],
  providers: [
    {
      provide: UserService,
      useFactory: serviceFactory,
      deps: [ApplicationHostTypeService, BrokerUserService, DirectUserService]
    },
    {
      provide: StateService,
      useFactory: stateServiceFactory,
      deps: [ApplicationHostTypeService, BrokerStateService, DirectStateService]
    },
    {
      provide: TaggingService,
      useFactory: taggingServiceFactory,
      deps: [ApplicationHostTypeService, BrokerTaggingService, DirectTaggingService]
    },
    RedirectFromOldAppService
  ]
})
export class CoreModule {}
